import {Typography} from "@mui/material"
import makeStyles from '@mui/styles/makeStyles';

import React, {useEffect, useState} from "react"
import SectionHeader from "../../../app/components/SectionHeader"
import ScrollableSectionView from "../../../app/ScrollableSectionView"
import {SEASONAL_PASSES_BASE_URL} from "../../../app/Routes"
import {ProgressIndicator} from "../../../app/ProgressIndicator"
import {useMuseumSelectorState} from "../../../app/museum-selector/museumSelectorContext"
import {useAxiosBpn} from "../../../app/axios"
import useCustomSnackbar from "../../../app/components/useCustomSnackbar"
import {useAuthsState} from "../../../app/auths/authsContext"
import DeleteConfirmationDialog from "../../../app/components/DeleteConfirmationDialog"
import {useSeasonalPassesTranslation} from "../membershipsTranslationContext"
import {SeasonalPassCard} from "./SeasonalPassCard"
import DropdownSelectorFilter from "../../../app/components/DropdownSelectorFilter"
import DebouncedTextFilter from "../../../app/components/DebouncedTextFilter"
import {
    PRODUCT_STATUS_DRAFT,
    PRODUCT_STATUS_PUBLISHED,
    SELECTABLE_PRODUCT_STATUSES,
} from "../../../app/helper-functions/productStatuses"
import TestOrdersSwitch from "../../../app/components/TestOrdersSwitch"

const LOCAL_STORAGE_KEY = "bpn.testOrders"

const useStyles = makeStyles(theme => ({
    filterSectionContainer: {
        display: "flex",
        marginBottom: theme.spacing(1),
    },
    filterContainer: {
        display: "flex",
        alginItems: "center",
        marginRight: theme.spacing(2),
    },
    textField: {
        fontWeight: 500,
        background: "white",
    },
}))

export const SeasonalPassesList = () => {
    const classes = useStyles()
    const t = useSeasonalPassesTranslation()
    const {umbrellaAndChildrenIds, selectedMuseum} = useMuseumSelectorState()
    const {userAccessMuseumIds} = useAuthsState()
    const snackbar = useCustomSnackbar()

    const [selectedStatuses, setSelectedStatuses] = useState([PRODUCT_STATUS_DRAFT, PRODUCT_STATUS_PUBLISHED])
    const [searchValue, setSearchValue] = useState("")
    const [productSeasonalPasses, setProductSeasonalPasses] = useState([])
    const [selectedProductId, setSelectedProductId] = useState()
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const initialTestOrders = localStorage.getItem(LOCAL_STORAGE_KEY) === "true"
    const [testOrders, setTestOrders] = useState(initialTestOrders)

    const [{data, loading}, getSeasonalPasses] = useAxiosBpn({
        url: "seasonal_passes",
        params: {
            museumIds: umbrellaAndChildrenIds?.length
                ? umbrellaAndChildrenIds.join()
                : Array.from(userAccessMuseumIds.keys()).join(),
            test: testOrders,
            statuses: selectedStatuses.join()
        },
    })

    const [{data: patchData, loading: patchLoading, error: patchError}, patchProduct] = useAxiosBpn({
        method: "PATCH",
    }, {manual: true})

    const [{data: deleteData, loading: deleteLoading, error: deleteError}, deleteEvent] = useAxiosBpn({
        method: "DELETE",
    }, {manual: true})

    useEffect(() => {
        if (data?.seasonalPasses) {
            let filteredResult = data.seasonalPasses
                .filter(seasonalPass => selectedStatuses.includes(seasonalPass.status))
            if (searchValue) {
                filteredResult = filteredResult
                    .filter(seasonalPass => seasonalPass.name.toUpperCase()
                        .includes(searchValue.toUpperCase()))
            }
            setProductSeasonalPasses(filteredResult)
        }
    }, [selectedStatuses, searchValue, data])

    useEffect(() => {
        getSeasonalPasses().catch(() => {
            // Just to avoid annoying console error
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (deleteData) {
            snackbar.showSuccess(`${t("seasonalPass")} ${t("deleted").toLowerCase()}`)
            getSeasonalPasses().catch(() => {
                // Just to avoid annoying console error
            })
        }
    }, [deleteData, getSeasonalPasses, snackbar, t])

    useEffect(() => {
        if (patchData) {
            snackbar.showSuccess(`${t("seasonalPass")} ${t("archived").toLowerCase()}`)
            getSeasonalPasses().catch(() => {
                // Just to avoid annoying console error
            })
        }
    }, [getSeasonalPasses, patchData, snackbar, t])

    useEffect(() => {
        if (deleteError) snackbar.showError(t(deleteError.message))
    }, [deleteError, snackbar, t])

    useEffect(() => {
        if (patchError) snackbar.showError(t(patchError.message))
    }, [patchError, snackbar, t])

    useEffect(() => localStorage.setItem(LOCAL_STORAGE_KEY, testOrders.toString()), [testOrders])

    const breadcrumbs = [
        {label: t("seasonalPass"), url: SEASONAL_PASSES_BASE_URL},
        {label: t("list")},
    ]

    const handleDeleteButtonClick = productId => {
        setSelectedProductId(productId)
        setDeleteDialogOpen(true)
    }

    const handleDelete = productId => {
        setDeleteDialogOpen(false)
        deleteEvent({url: `products/${productId}`})
    }

    const handleArchiveButtonClick = (seasonalPass, setAsArchived) => {
        setSelectedProductId(seasonalPass.productId)
        const status = setAsArchived ? "archived" : "draft"
        patchProduct({url: `products/${seasonalPass.productId}`, params: {status: status}})
    }

    const handleStatusFilterChange = (event) => {
        const value = event.target.value
        setSelectedStatuses(value)
    }

    const onNameFilterValueChange = event => {
        setSearchValue(event.target.value)
    }

    return (
        <>
            <SectionHeader breadcrumbs={breadcrumbs}>
                <TestOrdersSwitch testOrders={testOrders}
                                  setTestOrders={setTestOrders}/>
            </SectionHeader>
            <div className={classes.filterSectionContainer}>
                <div className={classes.filterContainer}>
                    <DebouncedTextFilter onChange={onNameFilterValueChange}
                                         debounceTime={300}
                                         filterName={t("name")}/>
                </div>
                <div className={classes.filterContainer}>
                    <DropdownSelectorFilter onChange={handleStatusFilterChange}
                                            value={selectedStatuses}
                                            options={SELECTABLE_PRODUCT_STATUSES}/>
                </div>
            </div>
            <ScrollableSectionView>
                {loading || patchLoading || deleteLoading
                    ? <ProgressIndicator/>
                    : productSeasonalPasses?.length
                        ? productSeasonalPasses.map((seasonalPass) =>
                            <SeasonalPassCard key={seasonalPass.productId}
                                              seasonalPass={seasonalPass}
                                              roleValue={selectedMuseum?.role?.value}
                                              onArchiveButtonClicked={handleArchiveButtonClick}
                                              onDeleteButtonClicked={handleDeleteButtonClick}/>)
                        : <Typography style={{margin: "16px"}}>{t("noSeasonalPassesYet")}!</Typography>}
            </ScrollableSectionView>
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                content={`${t("deleteSeasonalPassDialogText")}?`}
                handleClose={() => setDeleteDialogOpen(false)}
                handleDelete={() => handleDelete(selectedProductId)}/>
        </>
    )
}

